import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, formatError } from "@/api";
import { toast } from "sonner";
import { Btn, Card, Tag, Spinner, StatusBadge } from "@/components/ui-kit";
import { EmptyState, DetailRow } from "@/components/dashboard-bits";
import { CreatorProfileView } from "@/components/CreatorProfileView";
import { AffiliateSummary } from "@/components/AffiliateCard";
import { ConversationPanel } from "@/components/ConversationPanel";
import { Wallet, MapPin, Users, Calendar, ArrowLeft, MessageSquare } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fi-FI", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(undefined);
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;
    api.post(`/applications/${id}/read`).catch(() => {});
    api.get("/applications/received").then(async (r) => {
      const found = (r.data || []).find((a) => a.application_id === id) || null;
      if (!active) return;
      setApp(found);
      if (found?.campaign_id) {
        try { const c = await api.get(`/campaigns/${found.campaign_id}`); if (active) setCampaign(c.data); }
        catch { /* campaign not available */ }
      }
    }).catch(() => { if (active) setApp(null); });
    return () => { active = false; };
  }, [id]);

  if (app === undefined) return <Spinner />;

  const creatorId = app?.creator?.user_id || app?.creator_id;
  const setStatus = async (status) => {
    try {
      const { data } = await api.put(`/applications/${app.application_id}/status`, { status });
      setApp({ ...app, status: data.status });
      toast.success(status === "accepted" ? "Hakemus hyväksytty" : "Hakemus hylätty");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl mx-auto pb-16" data-testid="application-detail-page">
      <button onClick={() => navigate(-1)} data-testid="back-button"
        className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-[#6E675C] hover:text-[#292827] transition-colors">
        <ArrowLeft size={16} /> Takaisin
      </button>

      {app === null ? (
        <EmptyState title="Hakemusta ei löytynyt" subtitle="Hakemus ei ole saatavilla." />
      ) : (
        <>
          {/* 1. Kampanjan nimi */}
          <div className="text-[13px] text-[#968F85]">{app.campaign_kind === "affiliate" ? "Affiliate-ohjelma" : "Kampanja"}</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-display text-[#292827]" data-testid="application-campaign-title">{app.campaign_title || campaign?.title || "Kampanja"}</h1>

          {/* 2. Kampanjan tiedot */}
          {campaign?.kind === "affiliate" && <AffiliateSummary c={campaign} />}
          {campaign && campaign.kind !== "affiliate" && (
            <Card className="mt-6 p-6" data-testid="application-campaign-info">
              {campaign.description && <p className="text-[15px] text-[#3A3733] leading-relaxed line-clamp-4">{campaign.description}</p>}
              <div className="mt-5 grid sm:grid-cols-2 gap-5">
                <DetailRow icon={Wallet} label="Budjetti" value={campaign.budget || (campaign.budget_from && campaign.budget_to ? `${campaign.budget_from}–${campaign.budget_to} €` : "—")} />
                <DetailRow icon={MapPin} label="Toteutustapa" value={campaign.location || campaign.work_location || "—"} />
                <DetailRow icon={Users} label="Sisällöntuottajia haussa" value={campaign.creators_needed || "—"} />
                <DetailRow icon={Calendar} label="Hakuaika päättyy" value={campaign.application_deadline || "—"} />
              </div>
              {(campaign.categories?.length ? campaign.categories : [campaign.category]).filter(Boolean).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {(campaign.categories?.length ? campaign.categories : [campaign.category]).filter(Boolean).map((t) => <Tag key={t}>{t}</Tag>)}
                </div>
              )}
            </Card>
          )}

          {/* 3. Sisällöntuottajan hakemus */}
          <Card className="mt-10 p-7 border-l-4 border-l-[#7EA8E6] bg-[#F6F8FB]" data-testid="application-message-section">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tightish text-[#292827]">Hakemus</h2>
              <span className="flex items-center gap-1.5 text-xs text-[#968F85] shrink-0"><Calendar size={13} /> {formatDate(app.created_at)}</span>
            </div>
            {app.message && <p className="mt-4 text-[17px] text-[#292827] leading-relaxed whitespace-pre-line">{app.message}</p>}
            {(app.expected_price || app.delivery_time) && (
              <div className="mt-5 flex flex-wrap gap-2 text-xs">
                {app.expected_price && <span className="rounded-full border border-[#DCE6F5] bg-white px-3 py-1.5 text-[#292827]">Hintapyyntö: {app.expected_price}</span>}
                {app.delivery_time && <span className="rounded-full border border-[#DCE6F5] bg-white px-3 py-1.5 text-[#292827]">Toimitus: {app.delivery_time}</span>}
              </div>
            )}
            {app.campaign_kind === "affiliate" && (
              <div className="mt-6 pt-5 border-t border-[#DCE6F5] flex flex-wrap items-center gap-3" data-testid="application-decision">
                <StatusBadge status={app.status} />
                <Btn variant="dark" className="!py-2.5" disabled={app.status === "accepted"} onClick={() => setStatus("accepted")} data-testid="accept-application">Hyväksy</Btn>
                <Btn variant="outline" className="!py-2.5" disabled={app.status === "rejected"} onClick={() => setStatus("rejected")} data-testid="reject-application">Hylkää</Btn>
              </div>
            )}
          </Card>

          {/* 4. Sisällöntuottajan koko profiili */}
          {app.creator && (
            <Card className="mt-10 p-7 sm:p-8" data-testid="application-creator-card">
              <CreatorProfileView creator={app.creator} />
            </Card>
          )}

          {/* 5. Keskustelu */}
          <div className="mt-12" data-testid="application-conversation-section">
            <h2 className="text-lg font-semibold tracking-tightish text-[#292827] flex items-center gap-2">
              <MessageSquare size={18} strokeWidth={1.75} /> Keskustelu
            </h2>
            <p className="mt-1 text-sm text-[#6E675C]">Sama viestiketju näkyy myös Viestit-sivulla.</p>
            <div className="mt-4">
              {creatorId && (
                <ConversationPanel
                  otherUserId={creatorId}
                  otherName={app.creator?.name || app.creator_name || "Sisällöntuottaja"}
                  campaignId={app.campaign_id}
                  canInitiate={true}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
